import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Scale, ArrowLeft, Shield, Lock, Database } from 'lucide-react';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <header className="fixed top-0 left-0 right-0 z-50">
        <div className="glass-card mx-6 my-4 px-6 py-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <Scale className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-blue-900">Advokat for alle</h1>
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbake
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <Shield className="w-12 h-12 text-blue-600" />
            <div>
              <h2 className="text-4xl font-bold text-blue-900">Personvernerklæring</h2>
              <p className="text-gray-600 mt-2">Sist oppdatert: Januar 2025</p>
            </div>
          </div>

          <div className="space-y-8">
            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">Introduksjon</h3>
              <p className="text-gray-700 mb-4">
                Advokat for alle tar ditt personvern på alvor. Denne erklæringen beskriver hvordan vi samler inn, bruker, lagrer og beskytter dine personopplysninger i samsvar med GDPR og norsk lov.
              </p>
              <p className="text-gray-700">
                Vi er dataansvarlig for behandlingen av dine personopplysninger.
              </p>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">Hvilke data samler vi inn?</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Kontaktinformasjon:</strong> Navn, e-post, telefonnummer</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Juridisk informasjon:</strong> Beskrivelse av din juridiske situasjon</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Tekniske data:</strong> IP-adresse, nettleserinformasjon (for sikkerhet)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Betalingsinformasjon:</strong> Håndteres av Stripe (vi lagrer ikke kortdetaljer)</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">Rettslig grunnlag</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>Avtale (Art. 6(1)(b)):</strong> Vi behandler dine data for å levere juridiske tjenester du har bestilt.
                </p>
                <p>
                  <strong>Samtykke (Art. 6(1)(a)):</strong> Du gir eksplisitt samtykke til bruk av AI-assistanse og eventuelle markedsføringsformål.
                </p>
              </div>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">
                <Database className="w-6 h-6 inline mr-2" />
                AI og databehandling
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Vi bruker GPT-5 (OpenAI) som et internt verktøy for å utarbeide juridiske analyser</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>AI-modellen trenes IKKE på dine data</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Alle AI-genererte svar gjennomgås og godkjennes av lisensiert advokat før utsendelse</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Redacted copies (anonymiserte versjoner) lagres for kvalitetssikring</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">
                <Lock className="w-6 h-6 inline mr-2" />
                Sikkerhet og lagring
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Region:</strong> Alle data lagres innenfor EØS-området</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Kryptering:</strong> TLS 1.3 under overføring, AES-256 ved lagring</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Oppbevaring:</strong> 24 måneder som standard, deretter automatisk sletting</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Tilgangskontroll:</strong> Rollebasert tilgang (RBAC) med minste privilegium</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">Dine rettigheter</h3>
              <p className="text-gray-700 mb-3">Under GDPR har du følgende rettigheter:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Innsyn:</strong> Få kopi av dine data</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Retting:</strong> Korrigere uriktige opplysninger</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Sletting:</strong> Be om sletting av dine data (med juridiske forbehold)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Dataportabilitet:</strong> Motta dine data i maskinlesbart format</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Trekke samtykke:</strong> Trekke ditt samtykke når som helst</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                For å utøve disse rettighetene, kontakt oss på{' '}
                <a href="mailto:personvern@advokatforalle.no" className="text-blue-600 hover:underline">
                  personvern@advokatforalle.no
                </a>
              </p>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">Databehandlere</h3>
              <p className="text-gray-700 mb-3">Vi bruker følgende underleverandører:</p>
              <ul className="space-y-2 text-gray-700">
                <li>• OpenAI (GPT-5) - AI-assistanse</li>
                <li>• Stripe - Betalingsbehandling</li>
                <li>• AWS / Azure (EEA-regioner) - Skylagring</li>
              </ul>
              <p className="text-gray-700 mt-3">
                Alle underleverandører er GDPR-kompatible og har signert databehandleravtaler.
              </p>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">Kontakt</h3>
              <p className="text-gray-700 mb-2">
                <strong>Dataansvarlig:</strong> Advokat for alle AS
              </p>
              <p className="text-gray-700 mb-2">
                <strong>E-post:</strong>{' '}
                <a href="mailto:personvern@advokatforalle.no" className="text-blue-600 hover:underline">
                  personvern@advokatforalle.no
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Datatilsynet:</strong> Du har rett til å klage til Datatilsynet hvis du mener vi ikke behandler dine data i henhold til loven.
              </p>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Privacy;